import { Avatar, Spin, Tree } from 'antd'
import { isNumber, isString } from 'lodash'
import { DataNode } from 'rc-tree/lib/interface'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { UserHoverCard, Watermark } from '@/components'
import {
  RequestedDepartmentWithMembersFragment,
  RequestedDepartmentWithMembersFragmentDoc,
  UserInfoFragment,
  useGetDepartmentsQuery,
  useGetMembersByDepartmentLazyQuery,
} from '@/generated/graphql'
import { routes } from '@/routes'

import s from './department-tree.module.less'

interface DepartmentTreeProps {
  isPage: boolean
  onCheck: (departmentIds: number[], userIds: number[]) => void
}

export const DepartmentTree: React.FC<DepartmentTreeProps> = ({ onCheck, isPage }) => {
  const { data, error, loading, client } = useGetDepartmentsQuery()
  const [getMembers] = useGetMembersByDepartmentLazyQuery()

  if (error) {
    return null
  }

  if (loading) {
    return (
      <div className={s.spinContainer}>
        <Spin size='large' />
      </div>
    )
  }

  const departments = (data ? data.departments : []).map((d) => {
    let members: UserInfoFragment[] = []
    try {
      const dd = client.cache.readFragment<RequestedDepartmentWithMembersFragment>({
        fragment: RequestedDepartmentWithMembersFragmentDoc,
        fragmentName: 'RequestedDepartmentWithMembers',
        id: client.cache.identify(d),
      })
      if (dd) {
        members = dd.members
      }
      // eslint-disable-next-line
    } catch {}
    return {
      title: d.name,
      key: d.departmentId,
      parentKey: d.parentDepartmentId,
      isLeaf: false,
      checkable: !['Agora.io', 'Agora China', 'Agora US'].includes(d.name),
      children: members.map(
        (u) =>
          ({
            title: (
              <UserHoverCard email={u.email}>
                {isPage ? <Link to={routes.users.page(u.email)}>{u.name}</Link> : u.name}
              </UserHoverCard>
            ),
            key: `${d.departmentId}-${u.userId}`,
            isLeaf: true,
            icon: <Avatar size={20} src={u.avatarUrl} />,
          }) as DataNode
      ),
    } as DataNode
  })

  departments.forEach((d) => {
    d.children = (d.children || []).concat(departments.filter((dd) => dd['parentKey'] === d.key))
  })

  const rootDepartment = departments.find((d) => d.key === 1)!
  const chinaDepartment = departments.find((d) => d.title === 'Agora China')

  const treeData = rootDepartment ? [rootDepartment] : []
  const defaultExpandedKeys = chinaDepartment ? [chinaDepartment.key] : []

  return (
    <Watermark>
      <div className={s.container}>
        <Tree
          className={s.tree}
          showIcon
          checkable
          multiple
          selectable={false}
          autoExpandParent
          treeData={treeData}
          defaultExpandedKeys={defaultExpandedKeys}
          onCheck={(keys) => {
            const departmentIds = (keys as unknown[]).filter(isNumber)
            const userIds = (keys as unknown[]).filter(isString).map((s) => Number(s.split('-')[1]))
            onCheck(departmentIds, userIds)
          }}
          loadData={async (node) =>
            getMembers({
              variables: {
                departmentId: Number(node.key),
              },
            })
          }
        />
      </div>
    </Watermark>
  )
}
